import { useCallback, useState } from "react";
import { leafPaths } from "./pathUtils";
import { coordinatorRegistry } from "./registry";
import { UnsavedChangesError } from "./UnsavedChangesError";
import { useEditor } from "./useEditor";

export function useSaveNow<
  TState extends Record<string, unknown>,
  TFields extends Record<string, unknown> = Partial<TState>,
>(entityId: string): { saveNow: (fields: Partial<TFields>) => Promise<void>; isSaving: boolean } {
  const { updateField, flushPending } = useEditor<TState, TFields>(entityId);
  const [isSaving, setIsSaving] = useState(false);

  const saveNow = useCallback(
    async (fields: Partial<TFields>) => {
      setIsSaving(true);
      updateField(fields);
      try {
        await flushPending();
      } catch (e) {
        const coordinator = coordinatorRegistry.get(entityId);
        const ownPaths = leafPaths(fields);
        if (coordinator && ownPaths.every((path) => coordinator.getFieldStatus(path) === "saved")) return;
        coordinator?.discardPendingChangesAt(ownPaths);
        throw e instanceof UnsavedChangesError && e.cause !== undefined ? e.cause : e;
      } finally {
        setIsSaving(false);
      }
    },
    [entityId, flushPending, updateField],
  );

  return { saveNow, isSaving };
}
